import { useState, useMemo, useCallback, useEffect } from "react";
import { clsx } from "clsx";
import { UnitPromotions } from "@/components/units/unit/info/UnitPromotions";
import { UnitDimension } from "@/components/units/unit/info/UnitDimension";
import { UnitFeatures } from "@/components/units/unit/info/UnitFeatures";
import { UnitUrgency } from "@/components/units/unit/info/UnitUrgency";
import { UnitPrice } from "@/components/units/unit/info/UnitPrice";
import { UnitImage } from "@/components/units/unit/info/UnitImage";
import { UnitBadge } from "@/components/UnitBadge";
import { WIDE_BADGE_SHAPES } from "@/components/UnitBadge/badgeShapes";
import { PromoExpandedContent } from "@/components/units/unit/info/PromoExpandedContent";
import { InlineReservationForm } from "@/components/units/unit/info/InlineReservationForm";
import type { UnitGroup } from "@/types";
import { WidgetLayout } from "@/types/enums";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useUnitDataAttributes } from "@/hooks/useUnitDataAttributes";
import { TriggerButtons } from "@/components/units/actions/TriggerButtons";
import { FindNearbyButton } from "@/components/units/unit/info/FindNearbyButton";
import { NearbyPanel } from "@/components/units/unit/info/NearbyPanel";
import { useOpenEsign } from "@/hooks/useOpenEsign.tsx";
import { UnitItemContext, type PromoExpandedBy } from "@/context/UnitItemContext";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useSmoothScrollIntoView } from "@/hooks/useSmoothScrollIntoView";
import { getFeaturedConfig } from "@/utils/units/featuredUnits";
import { getTextColorForBackground } from "@/utils/color/colorContrast";
import {
    Collapsible,
    CollapsibleContent,
} from "@/components/ui/collapsible";

interface UnitItemProps {
    unitGroup: UnitGroup;
    layout: "grid" | "row";
    /** Grid (both layouts): which unit's promo is expanded + the card-selected
     *  promo (lifted to UnitTable so the full-width sibling can submit it). */
    gridExpandedUnit?: {
        unitGroupId: number;
        expandedBy: PromoExpandedBy;
        selectedPromo: number;
    } | null;
    /** Grid (both layouts): expand/collapse a unit's promo panel; carries the
     *  card's current selectedPromo up for the sibling. */
    onGridPromoExpand?: (
        value: {
            unitGroupId: number;
            expandedBy: PromoExpandedBy;
            selectedPromo: number;
        } | null,
    ) => void;
    /** Grid: explicit grid-row/grid-column placement */
    gridStyle?: React.CSSProperties;
    /** Grid: render the Find Nearby button (sold-out unit with nearby units). */
    showNearbyButton?: boolean;
    /** Grid: render the nearby panel as a card overlay instead of a sibling. */
    nearbyAsOverlay?: boolean;
    /** Grid: whether this unit's nearby expansion is open. */
    isNearbyExpanded?: boolean;
    /** Grid: toggle this unit's nearby expansion. */
    onNearbyToggle?: () => void;
}

export const UnitItem = ({
    unitGroup,
    layout = "row",
    gridExpandedUnit,
    onGridPromoExpand,
    gridStyle,
    showNearbyButton = false,
    nearbyAsOverlay = false,
    isNearbyExpanded = false,
    onNearbyToggle,
}: UnitItemProps) => {
    const { representative_unit, rates, available } = unitGroup;
    const { widgetLayout, featuredUnits } = useDisplayOptionContext();

    // Featured "Tab" treatment: outline, background tint, and a vertical tab
    // ribbon all share the configured color; text contrast (black/white) is
    // derived from that color per ADA luminance rules. Only "tab" renders today.
    const featuredConfig = getFeaturedConfig(unitGroup, featuredUnits);
    const isFeaturedTab = featuredConfig?.treatment === "tab";
    const featuredStyle = isFeaturedTab
        ? ({
              "--featured-color": featuredConfig.color,
              // Prefer the host's accessible text color (WCAG-correct, matches the brand
              // --*-foreground vars); fall back to the local approximation.
              "--featured-text-color":
                  featuredConfig.colorForeground ??
                  getTextColorForBackground(featuredConfig.color),
              // Host-provided "-light" shade (matches the brand --*-light calc) for the row
              // tint; when absent, the host CSS color-mix fallback is used instead.
              ...(featuredConfig.colorLight
                  ? { "--featured-color-light": featuredConfig.colorLight }
                  : {}),
          } as React.CSSProperties)
        : undefined;

    // Badge treatment (WEB-1508): a colored badge overlaid on the unit image.
    // Badge-only — no row highlight (that stays with the Tab treatment). Requires
    // both a design and text; prefers the host's WCAG foreground for the label.
    const badge =
        featuredConfig?.treatment === "badge" &&
        featuredConfig.badgeDesign &&
        featuredConfig.badgeText
            ? {
                  shape: featuredConfig.badgeDesign,
                  color: featuredConfig.color,
                  text: featuredConfig.badgeText,
                  textColor: featuredConfig.colorForeground,
              }
            : null;

    const [selectedPromo, setSelectedPromo] = useState<number>(() => {
        if (rates.promo_rates.length === 1 || rates.promo_rates.length > 1) {
            // Non-null assertion - this will never be undefined.
            return rates.promo_rates[0]!.id;
        } else {
            return 0;
        }
    });

    // Grid (both layouts) uses lifted state from UnitTable; row layout uses local state.
    const isGridExpansion = layout === "grid" && onGridPromoExpand !== undefined;
    const [localPromoExpandedBy, setLocalPromoExpandedBy] =
        useState<PromoExpandedBy>(null);

    const promoExpandedBy = isGridExpansion
        ? (gridExpandedUnit?.unitGroupId === representative_unit.id
            ? gridExpandedUnit.expandedBy
            : null)
        : localPromoExpandedBy;

    const gridSetPromoExpandedBy = useCallback(
        (value: PromoExpandedBy) => {
            if (value === null) {
                onGridPromoExpand?.(null);
            } else {
                onGridPromoExpand?.({
                    unitGroupId: representative_unit.id,
                    expandedBy: value,
                    selectedPromo,
                });
            }
        },
        [onGridPromoExpand, representative_unit.id, selectedPromo],
    );

    const setPromoExpandedBy = isGridExpansion
        ? gridSetPromoExpandedBy
        : setLocalPromoExpandedBy;

    // Keep the lifted promo in sync when the card's "Choose Your Special" dropdown
    // changes while this card's grid expansion is already open, so the sibling
    // form submits the chosen promo. setGridExpandedUnit short-circuits the
    // same-unit case (no re-animation). INVARIANT: that same-unit branch must
    // never write selectedPromo, or this effect would loop.
    useEffect(() => {
        if (isGridExpansion && promoExpandedBy) {
            onGridPromoExpand?.({
                unitGroupId: representative_unit.id,
                expandedBy: promoExpandedBy,
                selectedPromo,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPromo]);

    useOpenEsign(unitGroup, selectedPromo);
    const dataAttributes = useUnitDataAttributes(unitGroup);

    const isLayout1 = widgetLayout === WidgetLayout.LAYOUT_1;
    const isLayout2 = widgetLayout === WidgetLayout.LAYOUT_2;
    // Market layout (WEB-704) reuses the Layout-2 row visuals (dimension overlay
    // badge, SALE ribbon) without the Layout-2 filter chrome.
    const isMarket = widgetLayout === WidgetLayout.MARKET;
    // Both layouts expand the row in place (Layout 1: WEB-1432, Layout 2: WEB-1436);
    // market keeps the same in-row multi-promo/action expansion.
    const inlineExpansionEnabled = isLayout1 || isLayout2 || isMarket;
    const hasMultiplePromos = rates.promo_rates.length > 1;
    // Quote and reserve both use the in-row stepped form flow.
    const isFormExpanded = promoExpandedBy === "quote" || promoExpandedBy === "reserve";
    // The expanded row hosts the multi-promo selector OR the inline action
    // form, so it can open even when there's a single promo.
    const canExpandRow = inlineExpansionEnabled && (hasMultiplePromos || isFormExpanded);
    // Layout 2 multi-promo is a two-step in-row flow: promo selector → action
    // form. Layout 1 picks the promo on the card ("Choose Your Special"
    // dropdown), so its expansion skips the selector and opens straight to the
    // form. Single/no-promo always skips straight to the form (no promo to pick).
    const [showActionForm, setShowActionForm] = useState(false);
    useEffect(() => {
        if (!isFormExpanded) setShowActionForm(false);
    }, [isFormExpanded]);
    const showFormStep =
        isFormExpanded && (isLayout1 || !hasMultiplePromos || showActionForm);
    const promoExpandedPanelId = `promo-panel-${representative_unit.id}`;

    // Scroll row expansion into view after it opens
    const rowExpansionRef = useSmoothScrollIntoView<HTMLDivElement>(
        !isGridExpansion && promoExpandedBy !== null,
    );

    const contextValue = useMemo(
        () => ({
            unitGroup,
            selectedPromo,
            setSelectedPromo,
            promoExpandedBy,
            setPromoExpandedBy,
            promoExpandedPanelId,
        }),
        [unitGroup, selectedPromo, setSelectedPromo, promoExpandedBy, setPromoExpandedBy, promoExpandedPanelId],
    );

    return (
        <ErrorBoundary>
            <UnitItemContext.Provider value={contextValue}
            >
                <div
                    className={clsx("unit-item", "unit-item--" + layout, {
                        "unit-item--featured": isFeaturedTab,
                    })}
                    data-state={
                        (inlineExpansionEnabled && promoExpandedBy) ||
                        (isLayout2 && isNearbyExpanded)
                            ? "open"
                            : undefined
                    }
                    style={{ ...gridStyle, ...featuredStyle }}
                    {...dataAttributes}
                >
                    {isFeaturedTab && (
                        <div className="unit-item__featured-tab">
                            <span className="unit-item__featured-tab-label">
                                {featuredConfig.label}
                            </span>
                        </div>
                    )}
                    <div className="unit-item__image">
                        <UnitImage
                            category={representative_unit.category}
                            length={representative_unit.length}
                            width={representative_unit.width}
                        />
                        {badge && (
                            <span
                                className={clsx("unit-badge-overlay", {
                                    // Layout 2 grid: badge sits on the left edge,
                                    // centered vertically, to clear the promo banner
                                    // that spans the top of the grid card.
                                    "unit-badge-overlay--layout-2-grid":
                                        isLayout2 && layout === "grid",
                                    // Layout 2 row: top-right corner with the per-shape
                                    // lift — wide shapes (angle/half-pill/tag) take the
                                    // standard lift; squarer/taller shapes lift a tad more.
                                    "unit-badge-overlay--layout-2-wide":
                                        isLayout2 &&
                                        layout === "row" &&
                                        WIDE_BADGE_SHAPES.has(badge.shape),
                                    "unit-badge-overlay--layout-2-tall":
                                        isLayout2 &&
                                        layout === "row" &&
                                        !WIDE_BADGE_SHAPES.has(badge.shape),
                                })}
                            >
                                <UnitBadge
                                    shape={badge.shape}
                                    color={badge.color}
                                    text={badge.text}
                                    textColor={badge.textColor}
                                    size={
                                        isLayout2
                                            ? 54
                                            : layout === "grid"
                                              ? 56
                                              : 64
                                    }
                                />
                            </span>
                        )}
                        {(isLayout2 || isMarket) && (
                            <div className="unit-item__dimension unit-item__dimension--overlay">
                                <UnitDimension unitGroup={unitGroup} />
                            </div>
                        )}
                    </div>
                    {!isLayout2 && !isMarket && (
                        <div className="unit-item__dimension">
                            <UnitDimension unitGroup={unitGroup} />
                        </div>
                    )}
                    <div className="unit-item__category">
                        {representative_unit.category?.name ?? ""}
                    </div>
                    <UnitFeatures features={representative_unit.features} />
                    <div className="unit-item__promotions">
                        <UnitPromotions
                            promotions={rates.promo_rates}
                            selectedPromo={selectedPromo}
                            onPromoChange={(promoId) =>
                                setSelectedPromo(promoId)
                            }
                            widgetLayout={widgetLayout}
                        />
                    </div>
                    <div className="unit-item__urgency">
                        <UnitUrgency available={available} />
                    </div>
                    <div className="unit-item__price">
                        <UnitPrice
                            unitGroup={unitGroup}
                            selectedPromo={selectedPromo}
                        />
                    </div>
                    <div className="unit-item__action">
                        <TriggerButtons
                            unitGroup={unitGroup}
                            selectedPromo={selectedPromo}
                        />
                        {showNearbyButton && (
                            <FindNearbyButton
                                isExpanded={isNearbyExpanded}
                                onClick={onNearbyToggle}
                            />
                        )}
                    </div>
                    {/* Row layout: expansion renders inside the card.
                        Grid layout: expansion renders as a sibling in UnitTable. */}
                    {canExpandRow && !isGridExpansion && (
                        <div ref={rowExpansionRef} className="unit-item__promotions-expanded accordion-container">
                            <Collapsible
                                open={promoExpandedBy !== null}
                                onOpenChange={(open) => {
                                    if (!open) setPromoExpandedBy(null);
                                }}
                            >
                                <CollapsibleContent
                                    className={clsx(
                                        "promotion-selection",
                                        showFormStep && "quote-selection",
                                    )}
                                    id={promoExpandedPanelId}
                                >
                                    {showFormStep ? (
                                        <InlineReservationForm
                                            mode={
                                                promoExpandedBy === "reserve"
                                                    ? "reserve"
                                                    : "lead"
                                            }
                                            unitGroup={unitGroup}
                                            selectedPromo={selectedPromo}
                                            onBack={
                                                // Layout 1 picks the promo on the card (no selector step), so there's nothing to go back to — no-op there.
                                                !isLayout1 && hasMultiplePromos
                                                    ? () =>
                                                          setShowActionForm(false)
                                                    : undefined
                                            }
                                        />
                                    ) : (
                                        <PromoExpandedContent
                                            promotions={rates.promo_rates}
                                            selectedPromo={selectedPromo}
                                            onPromoChange={setSelectedPromo}
                                            unitGroup={unitGroup}
                                            expandedBy={promoExpandedBy}
                                            onRequestQuoteForm={() =>
                                                setShowActionForm(true)
                                            }
                                            onRequestReserveForm={() =>
                                                setShowActionForm(true)
                                            }
                                        />
                                    )}
                                </CollapsibleContent>
                            </Collapsible>
                        </div>
                    )}
                    {/* Overlay mode (layout 1 grid, 3-4 cols): the nearby panel
                        is anchored to the card and drops below it so neighbouring
                        cards never reflow. Sibling mode renders in UnitTable. */}
                    {nearbyAsOverlay && isNearbyExpanded && (
                        <div className="unit-item__nearby unit-item__nearby-overlay">
                            <NearbyPanel unitGroup={unitGroup} />
                        </div>
                    )}
                </div>
            </UnitItemContext.Provider>
        </ErrorBoundary>
    );
};
